import type { Scene } from 'phaser';
import { MidiEventState } from 'Types';
import { Timestamp } from 'Types/ExerciseData';
import type { PlaybackPhase } from 'Phaser/PlaybackMachine/playbackMachine.types';

export type StartPlayingArgs = {
  measureOffset: number;
  processEventsQueue: [() => void, unknown];
};

export type AdaptiveTempoState = {
  state: 'started' | 'completed' | 'progress';
  value: number;
  isBottomPhrase?: boolean;
};

export enum AppTourStepHelper {
  ExpandTempoControls = 'expand-tempo-controls',
  EndTour = 'end-tour',
}

export type ToReactEvents = {
  ObjectiveComplete: { jumpTo: Timestamp };
  ExercisePassed: void;
  ExercisePaused: { lastPlayedMeasure?: number; pausedAtTimestamp?: number };
  ExerciseResumed: void;
  PhaserReady: void;
  CurrentSceneReady: Scene;
  ShowExercise: void;
  ObjectiveSkipped: { jumpTo: Timestamp; isLastObjective: boolean };
  HideExercise: void;
  SkipButtonRequest: void;
  StartedPlaying: StartPlayingArgs | void;
  ShowToast: string;
  HideToast: void;
  SetPhrasesTillNextTier: { phrasesTillNextTier: number };
  SetPhraseEligible: { eligible: boolean };
  ShowFeedbackToast: { state: MidiEventState };
  RepertoireCompleted: void;
  RepertoirePhraseCompleted: { phraseIndex: number };
  SetNotePosDummy: { x: number; y: number };
  SetAdaptiveTempoState: AdaptiveTempoState;
  SuggestLevelChange: { performance: 'good' | 'poor'; customGoal?: boolean };
  AdaptiveTempoRegionCreated: 'speed-up' | 'slow-down';
  UpdateTempo: { tempo: number };
  /** Legacy: emitted by NoteSequenceExercise when exercise ends. */
  ExerciseEnded: void;
  /** Legacy: emitted by NoteSequenceExercise on fail. */
  ExerciseFail: void;
  AppTourHelper: { step: AppTourStepHelper };
  SetLoopBounds: { startTimestamp: number; endTimestamp: number } | null;
  /** MUS-243: emitted by the scene's playback machine on every state
   *  transition. Container listens and mirrors into Redux `playbackPhase`.
   *  `phase` reuses the shared {@link PlaybackPhase} type so this contract
   *  stays in sync with the machine's state names. */
  PlaybackPhaseChanged: {
    phase: PlaybackPhase;
    isLoopingActive: boolean;
    hasEverPlayedInThisLoop: boolean;
  };
  /** `sequenceId` is a monotonic per-request id assigned by the PhraseManager.
   *  React echoes it back on the matching PhraseReady so the manager can restore
   *  request order for the initial fill (responses may resolve out of order).
   *
   *  `complexity` is the raw NWCS band the phrase should be drawn from, set only
   *  when complexity adjustment is active and its session map is loaded. React
   *  forwards it verbatim to gen-phrase as `complexityMin`/`complexityMax`; when
   *  absent the backend selects as it did pre-feature. */
  FetchPhrase: {
    excludeFirstNoteRest: boolean;
    sequenceId: number;
    complexity?: { min: number; max: number };
  };
  /** The scene has built a ComplexityController and wants the session map for
   *  the tier it is about to play.
   *
   *  PULL, not push. React owns the tier-complexity maps and caches them; the
   *  scene asks on every controller construction and React answers from cache,
   *  or fetches first on a miss. This is deliberately the inverse of the
   *  original design, where React pushed the map once when its fetch resolved:
   *  a controller rebuilt after that push (scene restart, tier change) got
   *  nothing, because the one payload had already been consumed by the
   *  controller it replaced.
   *
   *  Carries no tier identity — the scene does not know its level or tier, and
   *  React does. React resolves the current pair when it answers, which is also
   *  what makes the answer correct across a tier change: a scene-held copy could
   *  be stale, React's state cannot be. */
  RequestSessionComplexity: void;
  /** A complexity "rest phrase" started (`active: true`) or ended
   *  (`active: false`) playing. While active, React shows the "Breathe" message
   *  and dims the accuracy display; the scene has already stopped the rest
   *  phrase from counting toward accuracy. */
  SetRestPhraseActive: { active: boolean };
  /** The complexity band the phrase now playing was drawn from, in normalized
   *  [0, 1]. Drives the header's complexity progress bar.
   *
   *  Emitted only while complexity adjustment is active, at the start of each
   *  assessment phrase — rest phrases deliberately do not report, because they
   *  are drawn below the band without moving it, and the bar should show where
   *  the user *is*, not the dip. `null` clears the bar (feature off / session
   *  reset). */
  SetComplexityBand: { crMin: number; crMax: number } | null;
  /** Perfect Phrase Counter: the user just completed the qualifying streak of
   *  perfect loops, so the looped section is "perfected". The container shows a
   *  bottom toast in response. */
  SectionPerfected: void;
};
